'use client';

import { ExtendLogo } from '@jouzen/ecom-components/icons/ExtendLogo';
import { SelectionCard } from '@jouzen/ecom-components/v3/SelectionCard';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useFeatureGate } from '@statsig/react-bindings';
import { cx } from 'class-variance-authority';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { MessageKey } from 'types/LocaleMessageKey';

import { useAddOnBundleContext } from '@/app/[locale]/meta/_contexts/AddOnBundleProvider';
import upgradeWarrantyBenefits from '@/app/[locale]/store/_hooks/upgradeWarrantyBenefits';
import RichText from '@/app/components/RichText';
import WarrantySelectionEnum from '@/app/enums/WarrantySelectionEnum';
import useCurrency from '@/app/hooks/useCurrency';

const tValue = (hasMaxRingCount: boolean, t: (val: MessageKey) => string) => ({
  logo: function logo() {
    return (
      <ExtendLogo
        aria-label={t('extend')}
        className={cx('mb-1 ml-1 inline h-[16px] w-[75.2px]', {
          'opacity-20': hasMaxRingCount,
        })}
      />
    );
  },
});

const ExtendWarrantySection = () => {
  const t = useTranslations();
  const hsaFsaGate = useFeatureGate('hsafsa_rollout').value;
  const { currencyCode } = useCurrency();
  const {
    twoYearWarrantyData,
    threeYearWarrantyData,
    warrantySelection,
    onWarrantySelection,
    scrollRef,
  } = useAddOnBundleContext();

  const [twoYearOpen, setTwoYearOpen] = useState(false);
  const [threeYearOpen, setThreeYearOpen] = useState(false);

  const twoYearWarrantyPrice = twoYearWarrantyData?.price?.amount ?? 45;
  const threeYearWarrantyPrice = threeYearWarrantyData?.price?.amount ?? 60;

  const warranyBenefits = upgradeWarrantyBenefits(t);

  const handleAccordionClick = (section: 'two-year' | 'three-year') => {
    if (section === 'two-year') {
      setTwoYearOpen(!twoYearOpen);
    } else {
      setThreeYearOpen(!threeYearOpen);
    }
  };

  return (
    <>
      <div
        className={cx('pt-5 opacity-50 md:w-[1px] md:bg-sandstone-500 md:pt-0')}
      />
      <div className="md:w-[46%]" data-cy="warranty-section">
        <Typography className="pb-3 text-xl font-medium md:pb-4" color={'dark'}>
          <span />
          <RichText
            messageKey="pdp_warranty_step_title_new"
            tValues={tValue(false, t)}
          />
        </Typography>
        <Typography className="pb-5 text-base font-medium" color={'dark'}>
          {hsaFsaGate
            ? t('bundle_modal_warranty_subtext_hsa_fsa')
            : t('bundle_modal_warranty_subtitle')}
        </Typography>
        <div ref={twoYearOpen ? scrollRef : null}>
          <SelectionCard
            data-cy="bundle-warranty-two-year"
            AccordionContent={warranyBenefits}
            accordionOpen={twoYearOpen}
            cardSelected={warrantySelection == WarrantySelectionEnum.TwoYear}
            currencyCode={currencyCode}
            price={twoYearWarrantyPrice}
            title={t('2_year_protection_plan')}
            accordionOnClick={() => handleAccordionClick('two-year')}
            onCheckBoxClick={() =>
              onWarrantySelection({
                warrantyInput: WarrantySelectionEnum.TwoYear,
              })
            }
          />
        </div>
        <div ref={threeYearOpen ? scrollRef : null}>
          <SelectionCard
            data-cy="bundle-warranty-three-year"
            AccordionContent={warranyBenefits}
            accordionOpen={threeYearOpen}
            cardSelected={warrantySelection == WarrantySelectionEnum.ThreeYear}
            currencyCode={currencyCode}
            price={threeYearWarrantyPrice}
            title={t('3_year_protection_plan')}
            accordionOnClick={() => handleAccordionClick('three-year')}
            onCheckBoxClick={() =>
              onWarrantySelection({
                warrantyInput: WarrantySelectionEnum.ThreeYear,
              })
            }
          />
        </div>
        {hsaFsaGate && (
          <div>
            <Typography className="pt-5 text-base font-medium" color={'dark'}>
              <em>
                <RichText messageKey="bundle_modal_warranty_hsa_fsa" />
              </em>
            </Typography>
          </div>
        )}
      </div>
    </>
  );
};
export default ExtendWarrantySection;
